import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Factory, Flame, CheckCircle2, ShieldCheck, Users, ArrowRight } from 'lucide-react';
import ProductGallery from '../components/ProductGallery';
import { Link } from 'react-router-dom';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section id="home" className="relative bg-gradient-to-br from-orange-50 via-white to-orange-50 py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold">
                  Leading Manufacturer Since Excellence
                </span>
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Premium Specialty <span className="text-orange-600">Adhesive Tapes</span> for Industry
              </h1>
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                Shriram Spectape delivers high-performance adhesive solutions for industrial, automotive, and electronics applications. Trusted for contract manufacturing, high-temperature resistance, and robust quality.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/products">
                  <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg">
                    Explore Products
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg">
                    Request Quote
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-1 gap-6">
                <img 
                  src="/site-assets/hero.jpg" 
                  alt="Shriram Spectape Product Range" 
                  className="rounded-lg shadow-xl w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section id="features" className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-orange-600">Shriram Spectape</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Industry-leading adhesive solutions backed by decades of expertise and innovation
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-orange-300 transition-all hover:shadow-lg">
              <CardHeader>
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Factory className="h-7 w-7 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Contract Manufacturing</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Customized adhesive tape solutions tailored to your specific industrial requirements with flexible production capabilities.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-orange-300 transition-all hover:shadow-lg">
              <CardHeader>
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Flame className="h-7 w-7 text-orange-600" />
                </div>
                <CardTitle className="text-xl">High Temperature Resistance</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Engineered to withstand extreme temperatures from -20°C to +260°C for demanding industrial applications.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-orange-300 transition-all hover:shadow-lg">
              <CardHeader>
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <ShieldCheck className="h-7 w-7 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Robust Quality Standards</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Rigorous quality control and testing ensure consistent performance across all product lines and applications.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-orange-300 transition-all hover:shadow-lg">
              <CardHeader>
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-7 w-7 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Wide Range of Applications</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Comprehensive portfolio covering electrical insulation, sealing, mounting, packaging, and specialized industrial needs.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-orange-300 transition-all hover:shadow-lg">
              <CardHeader>
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-7 w-7 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Expert Technical Support</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Dedicated team providing guidance on tape selection, application methods, and custom solution development.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-orange-300 transition-all hover:shadow-lg">
              <CardHeader>
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-7 w-7 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Certified & Compliant</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Meeting international quality standards with materials tested for safety, durability, and environmental compliance.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Product Gallery Slideshow */}
      <section className="py-0">
        <ProductGallery />
      </section>

      {/* Industries Served */}
      <section id="industries" className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Industries We <span className="text-orange-600">Serve</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by leading manufacturers across diverse sectors
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Factory className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">Industrial</h3>
              <p className="text-gray-600 text-center">
                Heavy-duty tapes for manufacturing, assembly, packaging, and industrial processing applications requiring strength and durability.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <svg className="h-8 w-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">Automotive</h3>
              <p className="text-gray-600 text-center">
                Specialized tapes for wire harnessing, component mounting, interior assembly, and high-temperature automotive applications.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <svg className="h-8 w-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">Electronics</h3>
              <p className="text-gray-600 text-center">
                Precision insulation tapes for circuit boards, transformers, motors, and sensitive electronic component protection and assembly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Facility Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/site-assets/coater.jpg" 
                  alt="Shriram Spectape Manufacturing Facility and Coating Equipment" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <div className="inline-block">
                <span className="bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold">
                  Our Facility
                </span>
              </div>
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900">
                State-of-the-Art <span className="text-orange-600">Manufacturing Excellence</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our modern manufacturing facility in Bengaluru is equipped with advanced machinery and quality control systems to deliver precision-engineered adhesive tape solutions.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Advanced Production Lines</h4>
                    <p className="text-gray-600">Latest coating and converting equipment for consistent quality</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Quality Assurance Lab</h4>
                    <p className="text-gray-600">In-house testing facility ensuring every roll meets specifications</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Scalable Capacity</h4>
                    <p className="text-gray-600">Flexible production to handle orders from small batches to large volumes</p>
                  </div>
                </div>
              </div>
              <Link to="/contact">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg mt-4">
                  Schedule a Visit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Discover how our specialty adhesive tapes can enhance your operations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg">
                View Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
